import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Video, Square, RotateCcw, Loader2 } from "lucide-react";

const MAX_SECONDS = 15;

async function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

export default function VideoRecorder({ value, onChange, disabled }) {
    const videoRef = useRef(null);
    const previewRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const [stage, setStage] = useState("idle"); // idle | ready | recording | encoding | done
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => () => stopStream(), []);

    const stopStream = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const prime = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 },
                audio: true,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
            setStage("ready");
        } catch (e) {
            toast.error("Accès caméra refusé", { description: "Autorise la caméra pour enregistrer." });
        }
    };

    const start = () => {
        if (!streamRef.current) return;
        chunksRef.current = [];
        const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
            ? "video/webm;codecs=vp9,opus"
            : "video/webm";
        const rec = new MediaRecorder(streamRef.current, { mimeType: mime, videoBitsPerSecond: 1_200_000 });
        rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
        rec.onstop = async () => {
            setStage("encoding");
            const blob = new Blob(chunksRef.current, { type: mime });
            if (blob.size > 4_500_000) {
                toast.error("Vidéo trop lourde", {
                    description: "Enregistre à nouveau — essaie 10 s maximum.",
                });
                setStage("ready");
                return;
            }
            const dataUrl = await blobToDataUrl(blob);
            onChange(dataUrl);
            if (previewRef.current) previewRef.current.src = dataUrl;
            setStage("done");
            stopStream();
        };
        recorderRef.current = rec;
        rec.start();
        setStage("recording");
        setElapsed(0);
        const t0 = Date.now();
        timerRef.current = setInterval(() => {
            const s = Math.floor((Date.now() - t0) / 1000);
            setElapsed(s);
            if (s >= MAX_SECONDS) stop();
        }, 200);
    };

    const stop = () => {
        if (recorderRef.current && recorderRef.current.state === "recording") {
            recorderRef.current.stop();
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const reset = () => {
        stopStream();
        onChange(null);
        setStage("idle");
        setElapsed(0);
    };

    return (
        <div className="space-y-3 border border-white/10 bg-[#0a0a0a] p-4">
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Avis vidéo · 15 s max
                </span>
                {stage === "recording" && (
                    <span className="font-mono text-xs text-[#FF2E2E]">
                        ● REC {elapsed}s / {MAX_SECONDS}s
                    </span>
                )}
            </div>

            <div className="relative aspect-video overflow-hidden border border-white/10 bg-black">
                {(stage === "idle" || stage === "done") && value ? (
                    <video
                        ref={previewRef}
                        src={value}
                        controls
                        playsInline
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <video
                        ref={videoRef}
                        muted
                        playsInline
                        className="h-full w-full object-cover"
                    />
                )}
                {stage === "idle" && !value && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs uppercase tracking-widest text-neutral-500">
                        Aucun enregistrement
                    </div>
                )}
                {stage === "encoding" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-sm text-white">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Encodage...
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {stage === "idle" && (
                    <Button
                        type="button"
                        onClick={prime}
                        disabled={disabled}
                        data-testid="video-prime"
                        className="h-10 rounded-none bg-[#FF2E2E] px-4 font-mono text-[11px] uppercase tracking-widest hover:bg-[#D61F1F]"
                    >
                        <Video className="mr-2 h-3.5 w-3.5" />
                        {value ? "Réenregistrer" : "Activer la caméra"}
                    </Button>
                )}
                {stage === "ready" && (
                    <Button
                        type="button"
                        onClick={start}
                        data-testid="video-record"
                        className="h-10 rounded-none bg-[#FF2E2E] px-4 font-mono text-[11px] uppercase tracking-widest hover:bg-[#D61F1F]"
                    >
                        <Video className="mr-2 h-3.5 w-3.5" />
                        Démarrer l&apos;enregistrement
                    </Button>
                )}
                {stage === "recording" && (
                    <Button
                        type="button"
                        onClick={stop}
                        data-testid="video-stop"
                        className="h-10 rounded-none border border-white/20 bg-black px-4 font-mono text-[11px] uppercase tracking-widest text-white hover:border-[#FF2E2E]"
                    >
                        <Square className="mr-2 h-3.5 w-3.5" />
                        Arrêter
                    </Button>
                )}
                {(stage === "done" || value) && stage !== "recording" && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={reset}
                        data-testid="video-reset"
                        className="h-10 rounded-none border border-white/10 bg-transparent px-4 font-mono text-[11px] uppercase tracking-widest text-neutral-300 hover:border-[#FF2E2E] hover:bg-transparent"
                    >
                        <RotateCcw className="mr-2 h-3.5 w-3.5" />
                        Retirer la vidéo
                    </Button>
                )}
            </div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Le consentement écrit de l&apos;élève est obligatoire avant publication.
            </p>
        </div>
    );
}
